import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { selectHasNoNonSampleMatters, selectHasOnlyLegacyUnassignedMatterExcludingSamples } from '@/matter/store/selectors'
import { useMattersForTable } from '@/matter/store/useMattersForTable'
import { MatterReadForTable, MatterType } from '@/matter/store/schemas'
import { RootState } from '@/store/store'
import { getBrandName } from '@/util/enterprise'
import { MatterDialogType, MattersActions } from '@/matter/store/slice'
import MatterOverflowMenu from './MatterOverflowMenu'
import { SearchField } from '@/components/search-field/SearchField'
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import MattersTableStatusView from './MattersTableStatusView'
import { SortableHeaderCell } from './SortableHeaderCell'
import { createColumnHelper, flexRender, getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable, SortingState } from '@tanstack/react-table'
import clsx from 'clsx'
import { SearchableText } from './SearchableText'
import { Tag } from '@/components/tag/Tag'
import { mattersMultiTermFilter } from '@/matter/utils/filter'
import { formatLastActivityForCell, formatLastActivityForTooltip } from '@/matter/utils/format-last-activity'
import { useNavigate } from 'react-router-dom'
import { kUnassignedMatterDescription } from '@/matter/constants'
import { isUnassignedMatter } from '@/matter/utils/is-unassigned-matter'
import { isPendingMatterInvite } from '@/matter/utils/is-pending-matter-invite'
import { matterPaths } from '@/routing/paths'
import { InfoCircleFilled } from '@/assets/icons'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover/Popover'
import { selectSettingsLoaded } from '@/chat-common/store/chat-ux.selectors'
import { useIsAccountCollaborationCapable } from '@/collaboration/hooks/useIsAccountCollaborationCapable'
import { canEditMatter } from '@/matter/utils/matter-permissions'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { matterCreatedBeforeLaunch } from '@/analytics/utils/matter-jurisdiction-analytics'
import { MatterJurisdictionEntrySource } from '@/analytics/enums/matter-jurisdiction.enums'

const columnHelper = createColumnHelper<MatterReadForTable>()

/**
 * Column IDs for the matters table.
 * Data columns use snake_case to match backend MatterRead field names.
 * Display columns use camelCase following TanStack Table and JavaScript conventions.
 */
enum MattersTableColumn {
  NAME = 'name',
  CLIENT = 'client',
  LAST_ACTIVITY_AT = 'last_activity_at',
  ACCESS_LEVEL = 'accessLevel',
  DESCRIPTION = 'description',
  OVERFLOW_MENU = 'overflowMenu', // camelCase for display-only column (not a data field)
}

// Applies truncation and vertical centering to text inside table data cells
const tableCellTextClass = 'inline-block max-w-full truncate whitespace-nowrap align-middle'

function MissingJurisdictionWarning({
  matterId,
  matterCreatedBeforeLaunch: wasCreatedBeforeLaunch,
  onEditMatter,
}: {
  matterId: string
  matterCreatedBeforeLaunch: boolean
  onEditMatter: (matterId: string) => void
}) {
  const brandName = getBrandName()
  const { trackEvent } = useAnalytics()
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="mr-1.5 inline-flex size-4 shrink-0 items-center justify-center text-yellow-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          aria-label="Missing default jurisdiction"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <InfoCircleFilled className="size-3.5" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        width="auto"
        side="top"
        align="start"
        alignOffset={-10}
        className="max-w-[431px] p-4"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <p className="text-sm font-normal leading-5 tracking-[-0.15px] text-brand-neutral-950">
          Add a primary jurisdiction from{' '}
          <button
            type="button"
            className="text-brand-500 underline"
            onClick={(e) => {
              e.stopPropagation()
              trackEvent(AnalyticsEvent.MatterJurisdictionTooltipLinkClicked, { matterId, matterCreatedBeforeLaunch: wasCreatedBeforeLaunch })
              onEditMatter(matterId)
            }}
          >
            Matter Details
          </button>{' '}
          to help {brandName} apply the correct laws and legal standards for this case.
        </p>
      </PopoverContent>
    </Popover>
  )
}

/**
 * Shared column widths used by both header and body tables.
 * Responsive percentage-based widths per breakpoint:
 *
 * Without Members column:
 *   xs (<640px):       Matter=60%, Client=40%, Last Activity=hidden, Context=hidden
 *   sm (640-767px):    All=25%
 *   md/lg (768-1279px): Matter=28%, Client=20%, Last Activity=23%, Context=29%
 *   xl (≥1280px):      Matter=30%, Client=17%, Last Activity=18%, Context=35%
 *
 * With Members column:
 *   xs (<640px):       Matter=60%, Client=40%, Last Activity=hidden, Members=hidden, Context=hidden
 *   sm (640-767px):    Matter=22%, Client=18%, Last Activity=18%, Members=10%, Context=32%
 *   md/lg (768-1279px): Matter=25%, Client=18%, Last Activity=20%, Members=10%, Context=27%
 *   xl (≥1280px):      Matter=26%, Client=16%, Last Activity=18%, Members=10%, Context=30%
 */
function MattersTableColumns({ showAccessColumn }: { showAccessColumn: boolean }) {
  if (showAccessColumn) {
    return (
      <colgroup>
        {/* Matter */}
        <col className="w-[60%] sm:w-[22%] md:w-[25%] xl:w-[26%]" />
        {/* Client */}
        <col className="w-[40%] sm:w-[18%] md:w-[18%] xl:w-[16%]" />
        {/* Last Activity */}
        <col className="hidden sm:table-column sm:w-[18%] md:w-[20%] xl:w-[18%]" />
        {/* Access Level */}
        <col className="hidden sm:table-column sm:w-[10%] md:w-[10%] xl:w-[10%]" />
        {/* Context */}
        <col className="hidden sm:table-column sm:w-[32%] md:w-[27%] xl:w-[30%]" />
        {/* Matter overflow menu column keeps a fixed narrow width */}
        <col className="w-12" />
      </colgroup>
    )
  }

  return (
    <colgroup>
      {/* Matter */}
      <col className="w-[60%] sm:w-1/4 md:w-[28%] xl:w-[30%]" />
      {/* Client */}
      <col className="w-[40%] sm:w-1/4 md:w-[20%] xl:w-[17%]" />
      {/* Last Activity */}
      <col className="hidden sm:table-column sm:w-1/4 md:w-[23%] xl:w-[18%]" />
      {/* Context */}
      <col className="hidden sm:table-column sm:w-1/4 md:w-[29%] xl:w-[35%]" />
      {/* Matter overflow menu column keeps a fixed narrow width */}
      <col className="w-12" />
    </colgroup>
  )
}

/**
 * Matters Table
 *
 * Displays a list of matters in a table format, with a sticky header and scrollable content.
 */
export function MattersTable() {
  const showAccessColumn = useIsAccountCollaborationCapable()

  // Redux state selectors
  const matters = useMattersForTable()
  const hasNoNonSampleMatters = useAppSelector(selectHasNoNonSampleMatters)
  const hasOnlyLegacyUnassignedMatterExcludingSamples = useAppSelector(selectHasOnlyLegacyUnassignedMatterExcludingSamples)
  const tableMatters = useMemo(
    () => (hasOnlyLegacyUnassignedMatterExcludingSamples ? matters.filter((matter) => matter.type !== MatterType.SAMPLE) : matters),
    [hasOnlyLegacyUnassignedMatterExcludingSamples, matters]
  )
  const isLoading = useAppSelector((state: RootState) => state.mattersState.loading)
  const initialLoadComplete = useAppSelector((state: RootState) => state.mattersState.initialLoadComplete)
  const settingsLoaded = useAppSelector(selectSettingsLoaded)

  // Inferred values
  const hasLoadedMatters = initialLoadComplete && matters.length > 0

  // Local state
  const [isOverflowScrollActive, setIsOverflowScrollActive] = useState(false)
  const [globalFilter, setGlobalFilter] = useState('')
  const [sorting, setSorting] = useState<SortingState>([{ id: MattersTableColumn.LAST_ACTIVITY_AT, desc: true }])

  // Refs
  const scrollingContainerRef = useRef<HTMLDivElement>(null)

  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  // Navigate to matter overview - also sets active matter in Redux for UI state consistency
  const navigateToMatterOverview = useCallback(
    (matterId: string) => {
      dispatch(MattersActions.setActiveMatterId({ matterId }))
      navigate(matterPaths.overview(matterId))
    },
    [dispatch, navigate]
  )

  // Only reached via the missing-jurisdiction tooltip's "Matter Details" link, so the entry source is fixed.
  const openEditMatterDialog = useCallback(
    (matterId: string) => {
      dispatch(MattersActions.openDialog({ dialogType: MatterDialogType.Edit, matterId, source: MatterJurisdictionEntrySource.AllMattersTooltip }))
    },
    [dispatch]
  )

  const openAcceptInviteDialog = useCallback(
    (matterId: string) => {
      dispatch(MattersActions.openDialog({ dialogType: MatterDialogType.AcceptInvite, matterId }))
    },
    [dispatch]
  )

  // Activating a row opens the matter — unless it's a pending invite, which must be accepted first.
  // Used by both the row's onClick and onKeyDown, so kept as a small named handler (no useCallback needed).
  const handleRowActivate = (matter: MatterReadForTable) => {
    if (isPendingMatterInvite(matter)) {
      openAcceptInviteDialog(matter.id)
    } else {
      navigateToMatterOverview(matter.id)
    }
  }

  const columns = useMemo(
    () => [
      columnHelper.accessor(MattersTableColumn.NAME, {
        header: 'Matter',
        meta: {
          headerClassName: 'pl-3 sm:pl-4 pr-4',
          cellClassName: 'pl-4 sm:pl-5 pr-4 py-4 relative',
          sortLabels: { ascendingLabel: 'Sort A-Z', descendingLabel: 'Sort Z-A' },
        },
        cell: ({ getValue, row, table }) => {
          const isActive = row.original.id === table.options.meta?.activeMatterId
          const isUnassigned = isUnassignedMatter(row.original)
          const isSampleMatter = row.original.type === MatterType.SAMPLE
          const isPendingInvite = isPendingMatterInvite(row.original)
          const canEdit = canEditMatter(row.original)
          const showJurisdictionWarning = settingsLoaded && !isUnassigned && !isPendingInvite && canEdit && !row.original.default_jurisdiction
          const value = getValue()
          const searchQuery = (table.getState().globalFilter as string) || ''

          return (
            <div className="relative flex min-w-0 items-center">
              {/* Dot indicator for the active matter */}
              {isActive && <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-[5px] h-[5px] bg-brand-500 rounded-full" />}
              {showJurisdictionWarning && (
                <MissingJurisdictionWarning
                  matterId={row.original.id}
                  matterCreatedBeforeLaunch={matterCreatedBeforeLaunch(row.original.created_at)}
                  onEditMatter={openEditMatterDialog}
                />
              )}
              <span title={value} className={tableCellTextClass}>
                <SearchableText text={value} searchQuery={searchQuery} />
              </span>
              {isUnassigned && (
                <span className="ml-2">
                  <Tag size="xs" variant="tonal" color="Neutral">
                    view only
                  </Tag>
                </span>
              )}
              {isSampleMatter && (
                <span className="ml-2">
                  <Tag variant="tonal" color="Green" size="xs">
                    Sample Matter
                  </Tag>
                </span>
              )}
            </div>
          )
        },
      }),
      columnHelper.accessor(MattersTableColumn.CLIENT, {
        header: 'Client',
        sortUndefined: 'last',
        meta: {
          headerClassName: 'pl-3 pr-4',
          cellClassName: 'p-4',
          sortLabels: { ascendingLabel: 'Sort A-Z', descendingLabel: 'Sort Z-A' },
        },
        cell: ({ getValue, row, table }) => {
          const value = getValue()
          const searchQuery = (table.getState().globalFilter as string) || ''

          if (isUnassignedMatter(row.original)) {
            return <span className={tableCellTextClass}>———</span>
          }

          return (
            <span title={value ?? undefined} className={tableCellTextClass}>
              {value ? <SearchableText text={value} searchQuery={searchQuery} /> : null}
            </span>
          )
        },
      }),
      columnHelper.accessor(MattersTableColumn.LAST_ACTIVITY_AT, {
        header: 'Last Activity',
        sortUndefined: 'last',
        meta: {
          headerClassName: 'pl-3 pr-4 hidden sm:table-cell',
          cellClassName: 'p-4 hidden sm:table-cell',
          sortLabels: { ascendingLabel: 'Sort old to new', descendingLabel: 'Sort new to old' },
          defaultSortDesc: true,
        },
        cell: ({ getValue, row }) => {
          if (isPendingMatterInvite(row.original)) {
            return <span className={tableCellTextClass}>———</span>
          }
          const value = getValue()
          return (
            <span title={formatLastActivityForTooltip(value)} className={tableCellTextClass}>
              {formatLastActivityForCell(value)}
            </span>
          )
        },
      }),
      ...(showAccessColumn
        ? [
            columnHelper.display({
              id: MattersTableColumn.ACCESS_LEVEL,
              header: 'Access',
              enableSorting: false,
              meta: { headerClassName: 'pl-3 pr-4 hidden sm:table-cell', cellClassName: 'p-4 hidden sm:table-cell' },
              cell: ({ row }) => {
                // Pending invites surface the accept affordance here instead of a member count.
                if (isPendingMatterInvite(row.original)) {
                  return <Tag size="xs">pending</Tag>
                }

                const memberCount = row.original.member_count
                if (memberCount === 1) return <span className={tableCellTextClass}>Only you</span>
                if (memberCount > 1) return <span className={tableCellTextClass}>{memberCount} members</span>

                return <span className={tableCellTextClass}>———</span>
              },
            }),
          ]
        : []),
      columnHelper.accessor(MattersTableColumn.DESCRIPTION, {
        header: 'Context',
        enableSorting: false,
        meta: { headerClassName: 'px-4 hidden sm:table-cell', cellClassName: 'p-4 hidden sm:table-cell' },
        cell: ({ getValue, row }) => {
          const value = getValue()

          if (isUnassignedMatter(row.original)) {
            return (
              <span title={kUnassignedMatterDescription} className={tableCellTextClass}>
                {kUnassignedMatterDescription}
              </span>
            )
          }

          return (
            <span title={value ?? undefined} className={tableCellTextClass}>
              {value}
            </span>
          )
        },
      }),
      columnHelper.display({
        id: MattersTableColumn.OVERFLOW_MENU,
        enableSorting: false,
        meta: { headerClassName: 'pr-2 sm:pr-3', cellClassName: 'pr-2 sm:pr-3 py-4 flex items-center justify-center' },
        cell: ({ row }) => (
          // Prevent row click/activation when interacting with the overflow menu
          <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <MatterOverflowMenu
              matterId={row.original.id}
              viewerRole={row.original.viewer_role}
              disabled={isUnassignedMatter(row.original) || isPendingMatterInvite(row.original)}
            />
          </div>
        ),
      }),
    ],
    [openEditMatterDialog, showAccessColumn, settingsLoaded]
  )

  const table = useReactTable({
    data: tableMatters,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: mattersMultiTermFilter,
    state: { globalFilter, sorting },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    getRowId: (row) => row.id,
  })

  const headerGroups = table.getHeaderGroups()
  const rows = table.getRowModel().rows // Filtered rows after applying search

  // Pin pending invites to the top so matters awaiting the user's acceptance surface first.
  // Array.sort is stable, so the active column sort is preserved within each group.
  const orderedRows = useMemo(() => [...rows].sort((a, b) => Number(isPendingMatterInvite(b.original)) - Number(isPendingMatterInvite(a.original))), [rows])

  const hasFilteredRows = rows.length > 0
  const shouldRenderDataRows = hasLoadedMatters && hasFilteredRows && !hasNoNonSampleMatters

  const hasSearchQuery = globalFilter.trim().length > 0
  const hasNoSearchResults = hasLoadedMatters && !hasFilteredRows && hasSearchQuery

  /**
   * Detects if the scroll container overflows vertically.
   * When it does, we remove the bottom border and radius so the table appears to scroll off the bottom of the page.
   */
  useLayoutEffect(() => {
    const el = scrollingContainerRef.current
    if (!el) return
    const update = () => setIsOverflowScrollActive(el.scrollHeight > el.clientHeight)

    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)

    return () => ro.disconnect()
  }, [rows.length, isLoading])

  return (
    <div className="flex flex-col gap-4 max-h-full overflow-hidden">
      <SearchField value={globalFilter} onChange={setGlobalFilter} placeholder="Search matters or clients" className="shrink-0" />
      <div
        className={clsx(
          'flex flex-col min-h-0 flex-1 overflow-hidden',
          isOverflowScrollActive ? 'rounded-t-md border-t border-l border-r' : 'rounded-md border'
        )}
      >
        {/* Sticky header rendered in its own table to prevent scrollbar from overlapping the header row */}
        <table className="w-full table-fixed border-collapse">
          <MattersTableColumns showAccessColumn={showAccessColumn} />

          {/* Header row */}
          <thead className="bg-brand-neutral-50 text-sm whitespace-nowrap border-b [&>tr>th]:py-1.5 [&>tr>th]:text-left [&>tr>th]:font-medium">
            {headerGroups.map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort()

                  // If the column is sortable, render a sortable header cell
                  if (canSort) {
                    return <SortableHeaderCell key={header.id} header={header} onSortingChange={setSorting} />
                  }

                  // Otherwise, render a regular header cell
                  return (
                    <th key={header.id} className={header.column.columnDef.meta?.headerClassName}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
        </table>

        {/* Scrollable body */}
        <div
          ref={scrollingContainerRef}
          className={clsx(
            'bg-white overflow-y-auto',
            // When showing data with overflow, allow flex shrinking
            isOverflowScrollActive && shouldRenderDataRows && 'flex-1 min-h-0'
          )}
        >
          {/* Table body with matters */}
          {shouldRenderDataRows && (
            <table className="w-full table-fixed border-collapse">
              <caption className="sr-only">List of matters</caption>
              <MattersTableColumns showAccessColumn={showAccessColumn} />
              {/* Use box-shadow equivalent to brand-neutral-200 to achieve inset borders */}
              <tbody className="text-sm [&>tr:not(:first-child)]:shadow-[inset_0_1px_0_0_rgb(240_235_226)]">
                {orderedRows.map((row) => {
                  return (
                    <tr
                      key={row.id}
                      className={clsx(
                        'cursor-pointer hover-capable:hover:bg-brand-neutral-100',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-700',
                        // Pending invites read as secondary until accepted; the bright "pending" tag still pops
                        // because the Tag sets its own text color and doesn't inherit this one.
                        isPendingMatterInvite(row.original) && 'text-brand-neutral-500'
                      )}
                      onClick={() => handleRowActivate(row.original)}
                      onKeyDown={(e) => {
                        // Enter activates the row: open the matter, or prompt to accept a pending invite
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleRowActivate(row.original)
                        }
                      }}
                      tabIndex={0}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className={cell.column.columnDef.meta?.cellClassName}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}

          {/* Status view based on the current state of the table - displayed when the table is loading, empty, has no search results, or has an error */}
          {(!shouldRenderDataRows || hasOnlyLegacyUnassignedMatterExcludingSamples) && (
            <div className={clsx(shouldRenderDataRows && hasOnlyLegacyUnassignedMatterExcludingSamples && 'border-t border-brand-neutral-200')}>
              <MattersTableStatusView searchQuery={globalFilter} hasNoSearchResults={hasNoSearchResults} onClearSearch={() => setGlobalFilter('')} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
